import React, { useCallback, useRef, useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GridItem } from './DashboardGrid'
import { PanelId } from './registry'
import ResizeGrip from './ResizeGrip'
import { PanelSpan } from './spans'

type Props = {
  id: PanelId
  span: PanelSpan
  children: (dragHandle: React.ReactNode) => React.ReactNode
  dragHandle: (props: {
    attributes: ReturnType<typeof useSortable>['attributes']
    listeners: ReturnType<typeof useSortable>['listeners']
  }) => React.ReactNode
  gridRef: React.RefObject<HTMLElement | null>
  onResize: (span: PanelSpan) => void
  resizeLabel: string
}

const SortableGridItem = ({ id, span, children, dragHandle, gridRef, onResize, resizeLabel }: Props) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id })
  const cellRef = useRef<HTMLDivElement | null>(null)
  const [previewSpan, setPreviewSpan] = useState<PanelSpan | null>(null)

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      cellRef.current = node
      setNodeRef(node)
    },
    [setNodeRef]
  )

  return (
    <GridItem
      ref={setRefs}
      span={previewSpan ?? span}
      style={{
        // `CSS.Translate`, never `CSS.Transform`: the strategy's scale ratio squashes neighbours on a
        // grid of differing widths.
        transform: CSS.Translate.toString(transform),
        transition: transition ?? undefined,
        // Hidden, not dimmed — `rectSortingStrategy` already slides this cell, so a visible one
        // showed two copies moving (ADR-027).
        opacity: isDragging ? 0 : 1,
      }}
    >
      {children(dragHandle({ attributes, listeners }))}
      <ResizeGrip
        cellRef={cellRef}
        gridRef={gridRef}
        onPreview={setPreviewSpan}
        onCommit={(next) => {
          if (next !== span) {
            onResize(next)
          }
        }}
        label={resizeLabel}
      />
    </GridItem>
  )
}

export default SortableGridItem
